import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { isBefore, isDate, subDays } from 'date-fns';
import { z } from 'zod';

import { useSliceWrapper } from '~/hooks/use-slice-wrapper';
import { mergeValues } from '~/utils/helpers';

const sliceStateSchema = z.object({
  user: z.object({
    type: z.coerce.number(),
    accessTokenExpiration: z
      .string()
      .datetime()
      .catch(subDays(new Date(), 1).toISOString()),
    refreshTokenExpiration: z
      .string()
      .datetime()
      .catch(subDays(new Date(), 1).toISOString()),
    ephemeralToken: z.string(),
    slug: z.string().nullable(),
    tenant_id: z.number().nullable(),
    isAccessAllowed: z.boolean(),
    isGoogleAccount: z.boolean().nullable(),
    isSessionExpired: z.boolean(),
    region: z.string().nullable(),
    ws: z.object({
      token: z.string(),
      refreshToken: z.string(),
    }),
  }),
  isInitialized: z.boolean().catch(false),
});

type SliceState = z.infer<typeof sliceStateSchema>;

const initialState: SliceState = {
  user: {
    type: 0,
    accessTokenExpiration: '',
    refreshTokenExpiration: '',
    ephemeralToken: '',
    slug: null,
    tenant_id: null,
    isGoogleAccount: false,
    isAccessAllowed: false,
    isSessionExpired: false,
    region: null,
    ws: {
      token: '',
      refreshToken: '',
    },
  },
  isInitialized: false,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AtLeastOne<T extends Record<string, any>> = keyof T extends infer K
  ? K extends string
    ? Pick<T, K & keyof T> & Partial<T>
    : never
  : never;
const STORAGE_KEY = '__session';
export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    initCredentials: (state) => {
      let storedState = initialState;
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
      const parsedState = sliceStateSchema.safeParse(stored);
      storedState = parsedState.success ? parsedState.data : initialState;

      // Backwards Compatibility
      const type = z.coerce
        .number()
        .catch(storedState.user.type)
        .parse(localStorage.getItem('user_type'));
      const slug = localStorage.getItem('slug') ?? '';
      const wsAccess =
        localStorage.getItem('ws_access') ?? storedState.user.ws.token;
      const accessExpiration = localStorage.getItem('access_expiration') ?? '';
      const refreshExpiration =
        localStorage.getItem('refresh_expiration') ?? '';
      const wsRefresh =
        localStorage.getItem('ws_refresh') ?? storedState.user.ws.refreshToken;

      const mergedState = mergeValues(initialState.user, storedState.user, {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        type,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        slug,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        ws: {
          token: wsAccess,
          refreshToken: wsRefresh,
        },
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        accessTokenExpiration: accessExpiration,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        refreshTokenExpiration: refreshExpiration,
      });

      const isSessionExpired =
        isDate(mergedState.accessTokenExpiration) &&
        isBefore(new Date(), new Date(mergedState.accessTokenExpiration));

      state.user = mergedState;
      state.user.isSessionExpired = isSessionExpired;
      state.isInitialized = true;
      // Backwards Compatibility
      // TODO: Remove this after all pages have been updated
      if (state.user.slug) {
        localStorage.setItem('slug', state.user.slug);
      }
      localStorage.setItem('user_type', state.user.type.toString());
      localStorage.setItem(
        'access_expiration',
        state.user.accessTokenExpiration,
      );
      localStorage.setItem(
        'refresh_expiration',
        state.user.refreshTokenExpiration,
      );

      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    },
    setCredentials: (
      state,
      action: PayloadAction<AtLeastOne<SliceState['user']>>,
    ) => {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      const _user = { ...state.user };
      state.user = { ..._user, ...action.payload };
      state.isInitialized = true;
      //TODO: `access` value might not be needed but will be retained for backwards compat
      const isAccessAllowed =
        !!state.user.accessTokenExpiration &&
        !!state.user.refreshTokenExpiration;
      state.user.isAccessAllowed = isAccessAllowed;

      const NODE_ENV = import.meta.env.VITE_NODE_ENV;
      if (
        NODE_ENV === 'dev' &&
        action.payload.ws?.token &&
        action.payload.ws?.refreshToken
      ) {
        state.user.ws = {
          token: action.payload.ws.token,
          refreshToken: action.payload.ws.refreshToken,
        };
      }

      // TODO: Remove this after all pages have been updated
      if (state.user.slug) {
        localStorage.setItem('slug', state.user.slug);
      }
      localStorage.setItem('user_type', state.user.type.toString());
      localStorage.setItem(
        'access_expiration',
        state.user.accessTokenExpiration,
      );
      localStorage.setItem(
        'refresh_expiration',
        state.user.refreshTokenExpiration,
      );

      // TODO: Remove this after all pages have been updated
      localStorage.setItem('ws_access', state.user.ws.token);
      localStorage.setItem('ws_refresh', state.user.ws.refreshToken);

      // Store tokens in localStorage or sessionStorage
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    },
    destroyCredentials: (state) => {
      const currentRegion = state.user.region;
      // TODO: Remove this after all pages have been updated
      localStorage.removeItem('slug');
      localStorage.removeItem('user_type');
      localStorage.removeItem('access_expiration');
      localStorage.removeItem('refresh_expiration');
      localStorage.removeItem('ws_access');
      localStorage.removeItem('ws_refresh');

      const newState: SliceState = {
        ...initialState,
        user: {
          ...initialState.user,
          /**
           * We retain the region value even if we are logged out
           * to cover cases where a request needs a `region` value
           * without having the user refresh the page to retrieve it
           * back again.
           */
          region: currentRegion,
          isSessionExpired: true,
        },
      };
      // Store tokens in localStorage or sessionStorage
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newState));
      return newState;
    },
  },
});

export const useAuthSlice = () => useSliceWrapper(authSlice, 'auth');
export const AuthStoreSlice = authSlice.actions;
